import { ContractTypes } from "@common/js/enum";
import { ISignalRHandler, SignalRInstance } from "@common/js/signalRClient";
import axios from "@common/js/axios";
import { Debounce, getDeepClone, i18nGlobal } from "@common/js/utils";
import { EnumRegisterDataType } from "@components/js/utils/model";
import _ from "lodash";
import { DataPoint, PolarDataPoint } from "@core/graphs/webcharts/Dh-webchart-models";
import store from "@common/js/store";

export const ChartRealType = {
	TimeDomain: 0, //时域图谱推送类型
	Spectral: 1, //频域图谱推送类型
	FreqIntegral: 2, // 频域积分
	CrossPowerSpecDens: 3, // 互功率谱密度
	AxisPosition: 4, //轴心位置
	AxisTrail: 5, //轴心轨迹
	BodePlots: 6, //伯德图,
	WholePeriod: 7, //整周期
	Polar: 8, //极坐标
	EigenBrowse: 9,
	AmplitudeAnalysis: 10, //幅值分析
	CablePre: 11,
	CepstrumAnalysis: 13, //倒谱分析
	CorrelationAnalysis: 14, //相关分析
	TorvibMonitoring: 15, //扭振监测
	Torque: 16, // 扭矩分析
	ShaftPower: 17, // 轴功率
	Recorder: 18,//记录仪Signalr
	RecorderReal: 19,//记录仪实时数据流
};

export const QueryEigenType = {
	Time: 0, //按时间
	Latest: 1, //查最近的一个值
};

/**图谱实时SignalR处理类 */
class GraphSignalrHandler extends ISignalRHandler {
	constructor() {
		super();
		this.graphHandlers = new Map();
		this.SupportContentType = [ContractTypes.Graph];
	}
	OnReconnected() {
		//发送注册实时数据
		for (const handler of this.graphHandlers.values()) {
			handler.sendRegisterPointsToClinet();
		}
	}
	OnConnected() {
		for (const handler of this.graphHandlers.values()) {
			handler.sendRegisterPointsToClinet();
		}
	}
	ReceiveData(data) {
		try {
			if (data.Content == null && data.Content.length == 0) {
				return;
			}
			let graphDatas = data.Content.map((c) => c.Data);
			for (const [key, value] of this.graphHandlers) {
				value.onDataReceived(graphDatas.filter((data) => data.Source.findIndex((r) => r == key) >= 0));
			}
			graphDatas = null;
		} catch (e) {
			debugger;
			console.log(e.message);
		}
	}
	registerHandler(chartRealType, handler) {
		this.graphHandlers.set(chartRealType, handler);
	}
	getHandler(chartRealType) {
		if (this.graphHandlers.has(chartRealType)) {
			return this.graphHandlers.get(chartRealType);
		}
		return null;
	}
	clear() {
		for (const handler of this.graphHandlers.values()) {
			handler.sendUnRegisterPointsToClient();
		}
		this.graphHandlers.clear();
	}
	IsSupport(contentType) {
		return this.SupportContentType.findIndex((r) => r == contentType) >= 0;
	}
}

/**图谱实时数据处理接口 */
class IGraphHandler {
	constructor() {
		/**图谱注册的数据回调委托 */
		this.dataReceiveds = new Map();
	}
	/**注册数据回调 */
	registerDataCalback(requestId, callback) {
		this.dataReceiveds.set(requestId, callback);
	}
	/**取消注册数据回调 */
	removeDataCalback(requestId) {
		this.dataReceiveds.delete(requestId);
		this.unRegisterPoints(requestId);
	}
	/**注册测点集合 */
	registerPoints(requestId, registerPoints) { }
	/**取消注册测点 */
	unRegisterPoints(requestId) { }
	/**接受实时数据 */
	onDataReceived(datas) { }
	sendRegisterPointsToClinet() { }
	sendUnRegisterPointsToClient() { }
}

class TimeDomainGraphHandler extends IGraphHandler {
	constructor() {
		super();
		this.graphRegisterPoints = new Map();
		this.registerPointCollection = new Map();
		this.pointChangeGraphs = new Map();
		this.pointHistoryChangeGraphs = new Map();
		this.canTrigger = true;
		this.stopTrigger = false;
		this.changeDatas = new Map();
		this.debounce = new Debounce();
	}
	registerPoints(requestId, registerPoints, changeData) {
		const points = new Map();
		if (registerPoints) {
			for (const registerPoint of registerPoints) {
				const valueList = JSON.parse(registerPoint.ValueList);
				let eigens = new Map();
				if (points.has(registerPoint.PointId)) eigens = points.get(registerPoint.PointId).Eigens;
				for (let value of valueList) {
					eigens.set(value.Id, null);
				}
				points.set(registerPoint.PointId, {
					PointId: registerPoint.PointId,
					DataType: registerPoint.DataType,
					Eigens: eigens,
				});
			}
		}
		this.graphRegisterPoints.set(requestId, points);
		if (changeData) this.changeDatas.set(requestId, changeData);
		this.pointChangeGraphs.set(requestId, _.cloneDeep(points));
		this.updateRegisterPointCollection();
		return true;
	}
	unRegisterPoints(requestId) {
		if (this.graphRegisterPoints.has(requestId)) {
			this.graphRegisterPoints.delete(requestId);
			this.updateRegisterPointCollection();
		}
		if (this.pointChangeGraphs.has(requestId)) {
			this.pointChangeGraphs.delete(requestId);
		}
		if (this.pointHistoryChangeGraphs.has(requestId)) {
			this.pointHistoryChangeGraphs.delete(requestId);
		}
	}
	updateRegisterPointCollection() {
		const registerPointCollection = new Map();
		for (const registerGraph of this.graphRegisterPoints.values()) {
			for (const registerPoint of registerGraph.values()) {
				if (!registerPointCollection.has(registerPoint.PointId)) {
					registerPointCollection.set(registerPoint.PointId, {
						Id: registerPoint.PointId,
						Eigens: new Array(),
						DataType: EnumRegisterDataType.Eigen,
					});
				}
				const point = registerPointCollection.get(registerPoint.PointId);
				for (const eigen of registerPoint.Eigens.keys()) {
					if (point.Eigens.findIndex((r) => r == eigen) < 0) {
						point.Eigens.push(eigen);
					}
				}
				if (registerPoint.DataType > EnumRegisterDataType.Eigen) {
					point.DataType = registerPoint.DataType;
				}
			}
		}
		this.registerPointCollection = registerPointCollection;
		this.sendRegisterPointsToClinet();
	}
	/**向后台发送注册测点信息 */
	sendRegisterPointsToClinet() {
		const signalR = SignalRInstance.GetInstance();
		if (!signalR) return;
		if ((signalR?.Hub?.connection?.connectionId != null) == false) return;
		this.debounce.run(() => {
			for (const requestId of this.pointChangeGraphs.keys()) {
				if (this.pointHistoryChangeGraphs.has(requestId)) {
					this.pointHistoryChangeGraphs.get(requestId).push(this.pointChangeGraphs.get(requestId));
				} else {
					this.pointHistoryChangeGraphs.set(requestId, [this.pointChangeGraphs.get(requestId)]);
				}
				this.pointChangeGraphs.delete(requestId);
			}
			const registerPointInfos = this.registerPointCollection.getValues().map((point) => {
				return {
					PointId: point.Id,
					EigenList: point.Eigens,
					RegisterDataType: point.DataType,
				};
			});
			const changeData = this.getChangeRegisterPoints();
			this.changeDatas.clear();
			if ((signalR?.Hub?.connection?.connectionId != null) == false) return;
			const request = this.createRegisterRequest({
				ConnectionId: signalR.Hub.connection.connectionId,
				PointRegisters: registerPointInfos,
				StartTime: changeData.startTime,
				NewPointRegisters: changeData.data,
				Source: ChartRealType.TimeDomain,
			})
			axios(request).then((res) => {
				if (res.IsSuccess && res.Result?.PointInfos) {
					this.processRegisterData(res.Result?.PointInfos);
				}
			});
		}, 100);
	}
	createRegisterRequest(data) {
		return {
			method: "post",
			url: "DataManage",
			data: data,
		}
	}
	processRegisterData(pointInfos) {
		let datas = new Array();
		for (const pointInfo of pointInfos) {
			const data = {
				PointId: pointInfo.PointId,
				AlgorithmType: pointInfo.AlgorithmType,
				Param: {
					PointId: pointInfo.PointId,
					Name: pointInfo.Name,
					Unit: pointInfo.Unit,
					FullValue: pointInfo.FullValue,
					EigenList: pointInfo.EigenList,
					AlarmRuler: pointInfo.AlarmRuler,
					IsObserver: pointInfo.IsObserver,
					Extensions: pointInfo.Extensions,
					IsCycle: pointInfo.IsCycle,
				},
				Eigens: [],
			};
			if (pointInfo.PointData == null) {
				data.Eigens = pointInfo.EigenList.map((eigen) => {
					return {
						EigenType: eigen.Key,
						Value: 0,
						Unit: eigen.Unit
					}
				});
			} else {
				data.EigenDatas = pointInfo.PointData.EigenDatas;
			}
			datas.push(data);
		}
		if (datas.length > 0) {
			this.processRegisterDataReceived(datas);
		}
		datas = null;
	}
	processRegisterDataReceived(datas) {
		this.processWaveData(datas);
		for (const requestId of this.dataReceiveds.keys()) {
			const handler = this.dataReceiveds.get(requestId);
			if (!this.pointHistoryChangeGraphs.has(requestId)) continue;
			const graphRegisterPoints = this.pointHistoryChangeGraphs.get(requestId).find(r => r.size > 0);
			if (graphRegisterPoints == null) continue;
			let graphDatas = [];
			for (const data of datas) {
				if (graphRegisterPoints.has(data.PointId)) {
					const point = graphRegisterPoints.get(data.PointId);
					graphRegisterPoints.delete(data.PointId);
					graphDatas.push(this.getCloneData(data, point));
				}
			}
			handler(graphDatas);
			graphDatas = null;
		}
		for (const key of this.pointHistoryChangeGraphs.keys()) {
			const graphRegisterPointArray = this.pointHistoryChangeGraphs.get(key);
			if (graphRegisterPointArray.length > 0) {
				let removes = graphRegisterPointArray.filter(r => r.size == 0);
				for (const r of removes) {
					graphRegisterPointArray.remove(r);
				}
			}
			if (graphRegisterPointArray.length == 0) {
				this.pointHistoryChangeGraphs.delete(key);
			}
		}
	}
	getChangeRegisterPoints() {
		const changePoints = new Array();
		let startTime = null;
		if (this.changeDatas.size > 0) {
			for (const graph of this.changeDatas.values()) {
				for (const point of graph.datas) {
					const pointInfo = {
						PointId: point.PointId,
						EigenList: JSON.parse(point.ValueList).map((e) => e.Id),
						RegisterDataType: point.DataType,
						QueryEigenType: point.QueryEigenType ?? QueryEigenType.Time,
					};
					changePoints.push(pointInfo);
				}
				startTime = graph.startTime;
			}
		}
		return {
			startTime: startTime,
			data: changePoints,
		};
	}
	sendUnRegisterPointsToClient() {
		axios({
			method: "delete",
			url: `DataManage/${SignalRInstance.GetInstance().SignalrHubId}`,
			data: {},
		}).then(() => { });
	}
	processWaveData(datas) {
		//let start = performance.now();
		for (const data of datas) {
			if (data.Datas?.length == 2) {
				data.Datas = this.getGraphDataPoints(data.Datas[0], data.Datas[1]);
			}
		}
		//let end = performance.now();
		//console.log('processWaveData', `${end - start}ms`)
	}
	onDataReceived(datas) {
		this.processWaveData(datas);
		for (const requestId of this.dataReceiveds.keys()) {
			const handler = this.dataReceiveds.get(requestId);
			if (!this.graphRegisterPoints.has(requestId)) continue;
			const graphRegisterPoints = this.graphRegisterPoints.get(requestId);
			let graphDatas = [];
			for (const data of datas) {
				if (graphRegisterPoints.has(data.PointId)) {
					const point = graphRegisterPoints.get(data.PointId);
					graphDatas.push(this.getCloneData(data, point));
				}
			}
			if (graphDatas.length > 0) {
				handler(graphDatas);
			}
			graphDatas = null;
		}
	}
	getCloneData(data, point) {
		const dataTemp = {
			PointId: data.PointId,
			SampleTime: data.SampleTime,
			SampleFreq: data.SampleFreq,
			PointDataType: data.PointDataType,
			AlgorithmType: data.AlgorithmType,
			AlarmParams: data.AlarmParams,
			Param: null,
			Eigens: [],
			Datas: null,
			XMax: data.XMax,
			EigenDatas: data.EigenDatas || [],
		};
		if (data.Param) {
			dataTemp.Param = getDeepClone(data.Param);
		}
		if (point.DataType == EnumRegisterDataType.EigenWave) {
			dataTemp.Datas = data.Datas;
		}
		if (data.Eigens) {
			dataTemp.Eigens = data.Eigens;
		}
		if (dataTemp.Param?.EigenList) {
			dataTemp.Param.EigenList = dataTemp.Param.EigenList.filter((r) => point.Eigens.has(r.Key));
		}
		return dataTemp;
	}
	getGraphDataPoints(type, data) {
		switch (type) {
			case 4:
				return getDataPointList(data);
			case 7:
				return getTrendPointList(data);
			case 8:
				return getFloatPointList(data);
			case 9:
				return getEnvelopFloatPointList(data);
			default:
				return data;
		}
	}
}

class BrowEigenHandler extends TimeDomainGraphHandler {
	constructor() {
		super();
	}
	sendRegisterPointsToClinet() {
		const signalR = SignalRInstance.GetInstance();
		if (!signalR) return;
		if ((signalR?.Hub?.connection?.connectionId != null) == false) return;
		this.debounce.run(() => {
			for (const requestId of this.pointChangeGraphs.keys()) {
				if (this.pointHistoryChangeGraphs.has(requestId)) {
					this.pointHistoryChangeGraphs.get(requestId).push(this.pointChangeGraphs.get(requestId));
				} else {
					this.pointHistoryChangeGraphs.set(requestId, [this.pointChangeGraphs.get(requestId)]);
				}
				this.pointChangeGraphs.delete(requestId);
			}
			let registerPointInfos = this.registerPointCollection.getValues().map((point) => {
				return {
					PointId: point.Id,
					EigenList: point.Eigens,
					RegisterDataType: EnumRegisterDataType.Eigen,
				};
			});
			this.changeDatas.clear();
			if ((signalR?.Hub?.connection?.connectionId != null) == false) return;
			axios({
				method: "post",
				url: "DataManage/browse",
				data: {
					ConnectionId: signalR.Hub.connection.connectionId,
					PointRegisters: registerPointInfos,
					Source: ChartRealType.EigenBrowse,
				},
			}).then((res) => {
				if (res.IsSuccess && res.Result?.PointInfos) {
					this.processRegisterData(res.Result?.PointInfos);
				}
			});
		}, 100);
	}
}

class SingleGraphHandlerBase extends IGraphHandler {
	constructor() {
		super();
		this.actionName = null;
		this.chartRealType = null;
		this.graphRegisterPoints = new Map();
	}
	registerPoints(requestId, registerPoints) {
		this.graphRegisterPoints.set(requestId, registerPoints);
		this.sendComponentRegiterPointsToClient(requestId);
	}
	unRegisterPoints(requestId) {
		this.sendComponentUnRegisterPointsToClient(requestId);
		this.graphRegisterPoints.delete(requestId);
	}
	onDataReceived(datas) {
		for (const key of this.dataReceiveds.keys()) {
			const handler = this.dataReceiveds.get(key);
			let arrays = datas.filter((r) => r.ComponentId == key);
			if (arrays.length > 0) {
				for (const data of arrays) {
					if (data.Datas?.length == 2) {
						data.Datas = this.getGraphDataPoints(data.Datas[0], data.Datas[1]);
					}
				}
				handler(arrays);
			}
			arrays = null;
		}
	}
	getGraphDataPoints(type, data) {
		switch (type) {
			case 4:
				return getDataPointList(data);
			case 5:
				return getPolarPointList(data);
			case 8:
				return getFloatPointList(data);
			case 9:
				return getEnvelopFloatPointList(data);
			default:
				return data;
		}
	}
	sendComponentRegiterPointsToClient(requestId) {
		if ((SignalRInstance.GetInstance()?.Hub?.connection?.connectionId != null) == false) return;
		const registerPoints = this.graphRegisterPoints.get(requestId);
		const postData = {};
		Object.assign(postData, registerPoints);
		postData.ComponentId = requestId;
		postData.ConnectionId = SignalRInstance.GetInstance().Hub.connection.connectionId;
		postData.Source = this.chartRealType;
		axios({
			method: "post",
			url: `Graphs/${this.actionName}`,
			data: postData,
		}).then((res) => {
			this.processRegisterData(res);
		});
	}
	processRegisterData(res) {
		if (res.IsSuccess) {
			const datas = new Array();
			if (res.Result?.Points)
				for (const pointInfo of res.Result.Points) {
					const data = {
						PointId: pointInfo.PointId,
						Param: {
							PointId: pointInfo.PointId,
							Name: pointInfo.Name,
							Unit: pointInfo.Unit,
							FullValue: pointInfo.FullValue,
							IsCycle: pointInfo.IsCycle,
						},
						ComponentId: res.Result.ComponentId,
					};
					datas.push(data);
				}
			if (datas.length > 0) {
				this.onDataReceived(datas);
			}
		}
	}
	sendComponentUnRegisterPointsToClient(requestId) {
		axios({
			method: "delete",
			url: `Graphs/${this.actionName}?id=${requestId}`,
			data: {},
		});
	}
	sendRegisterPointsToClinet() {
		for (const key of this.graphRegisterPoints.keys()) {
			this.sendComponentRegiterPointsToClient(key);
		}
	}
}

class SpectrumDomainGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "spectrum";
		this.chartRealType = ChartRealType.Spectral;
	}
}

class FreqIntegralGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "FreqIntegral";
		this.chartRealType = ChartRealType.FreqIntegral;
	}
}

class TorqueGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "torque";
		this.chartRealType = ChartRealType.Torque;
	}
}

class ShaftPowerGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "shaft-power";
		this.chartRealType = ChartRealType.ShaftPower;
	}
}

class CrossPowerSpectrumDensGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "cpsd";
		this.chartRealType = ChartRealType.CrossPowerSpecDens;
	}
}

class AxisPositionGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "axisPos";
		this.chartRealType = ChartRealType.AxisPosition;
	}
	processRegisterData(res) {
		if (res.IsSuccess) {
			const datas = new Array();
			if (res.Result?.Points)
				for (const pointInfo of res.Result.Points) {
					const data = {
						PointId: pointInfo.PointId,
						Param: {
							PointId: pointInfo.PointId,
							Name: pointInfo.Name,
							XUnit: pointInfo.XUnit,
							YUnit: pointInfo.YUnit,
							XFullValue: pointInfo.XFullValue,
							YFullValue: pointInfo.YFullValue,
						},
						ComponentId: res.Result.ComponentId,
					};
					datas.push(data);
				}
			if (datas.length > 0) {
				this.onDataReceived(datas);
			}
		}
	}
}

class AxisTrailGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "axisTrail";
		this.chartRealType = ChartRealType.AxisTrail;
	}
	processRegisterData(res) {
		if (res.IsSuccess) {
			const datas = new Array();
			if (res.Result?.Points)
				for (const pointInfo of res.Result.Points) {
					datas.push({
						PointId: pointInfo.PointId,
						Param: {
							PointId: pointInfo.PointId,
							Name: pointInfo.Name,
							XUnit: pointInfo.XUnit,
							YUnit: pointInfo.YUnit,
							XFullValue: pointInfo.XFullValue,
							YFullValue: pointInfo.YFullValue,
						},
						ComponentId: res.Result.ComponentId,
					});
					datas.push({
						PointId: pointInfo.XPointId,
						Param: {
							PointId: pointInfo.XPointId,
							Name: pointInfo.XName,
							Unit: pointInfo.XUnit,
							FullValue: pointInfo.XFullValue,
						},
						ComponentId: res.Result.ComponentId,
					});
					datas.push({
						PointId: pointInfo.YPointId,
						Param: {
							PointId: pointInfo.YPointId,
							Name: pointInfo.YName,
							Unit: pointInfo.YUnit,
							FullValue: pointInfo.YFullValue,
						},
						ComponentId: res.Result.ComponentId,
					});
				}
			if (datas.length > 0) {
				for (const key of this.dataReceiveds.keys()) {
					const handler = this.dataReceiveds.get(key);
					let arrays = datas.filter((r) => r.ComponentId == key);
					if (arrays.length > 0) {
						handler(arrays);
					}
					arrays = null;
				}
			}
		}
	}
	onDataReceived(datas) {
		for (const key of this.dataReceiveds.keys()) {
			const handler = this.dataReceiveds.get(key);
			const temp = datas.filter((r) => r.ComponentId == key);
			const arrays = [];
			for (const data of temp) {
				const datas = data.Datas?.length == 2 ? data.Datas[1] : data.Datas;
				arrays.push({
					PointId: data.PointId,
					ComponentId: data.ComponentId,
					SampleFreq: data.SampleFreq,
					SampleTime: data.SampleTime,
					Datas: {
						Points: datas.TDatas.map(data => {
							return new DataPoint(data.X, data.Y);
						}),
						DataCount: datas.TDatas.length
					},
				});
				if (datas.XDatas.Datas?.length == 2) {
					datas.XDatas.Datas = this.getGraphDataPoints(datas.XDatas.Datas[0], datas.XDatas.Datas[1]);
				}
				if (datas.YDatas.Datas?.length == 2) {
					datas.YDatas.Datas = this.getGraphDataPoints(datas.YDatas.Datas[0], datas.YDatas.Datas[1]);
				}
				arrays.push(datas.XDatas);
				arrays.push(datas.YDatas);
			}
			if (arrays.length > 0) {
				handler(arrays);
			}
		}
	}
}

class CablePreGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "cable";
		this.chartRealType = ChartRealType.CablePre;
	}
}

class BodePlotsGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "bode";
		this.chartRealType = ChartRealType.BodePlots;
	}
	processRegisterData(res) {
		if (res.IsSuccess) {
			const datas = new Array();
			if (res.Result?.Points) {
				for (let pointInfo of res.Result.Points) {
					datas.push(this.createData(pointInfo, res.Result.ComponentId, 1, i18nGlobal('core.amplitude')));
					datas.push(this.createData(pointInfo, res.Result.ComponentId, 2, i18nGlobal('core.phase')));
				}
			}
			if (datas.length > 0) {
				for (const key of this.dataReceiveds.keys()) {
					const handler = this.dataReceiveds.get(key);
					let arrays = datas.filter((r) => r.ComponentId == key);
					if (arrays.length > 0) {
						handler(arrays);
					}
					arrays = null;
				}
			}
		}
	}
	createData(pointInfo, componentId, idx, name) {
		return {
			PointId: pointInfo.PointId + "_" + idx,
			Param: {
				PointId: pointInfo.PointId + "_" + idx,
				Name: pointInfo.Name + "_" + name,
				Unit: pointInfo.Unit,
				FullValue: pointInfo.FullValue,
			},
			ComponentId: componentId,
		};
	}
	onDataReceived(datas) {
		for (const key of this.dataReceiveds.keys()) {
			const handler = this.dataReceiveds.get(key);
			const temp = datas.filter((r) => r.ComponentId == key);
			let arrays = [];
			for (const data of temp) {
				const datas = data.Datas?.length == 2 ? data.Datas[1] : data.Datas;
				datas.AmpDatas.PointId += "_1";
				datas.PhaseDatas.PointId += "_2";
				if (datas.AmpDatas.Datas?.length == 2) {
					datas.AmpDatas.Datas = this.getGraphDataPoints(datas.AmpDatas.Datas[0], datas.AmpDatas.Datas[1]);
				}
				if (datas.PhaseDatas.Datas?.length == 2) {
					datas.PhaseDatas.Datas = this.getGraphDataPoints(datas.PhaseDatas.Datas[0], datas.PhaseDatas.Datas[1]);
				}
				datas.AmpDatas.SampleTime = data.SampleTime;
				datas.PhaseDatas.SampleTime = data.SampleTime;
				arrays.push(datas.AmpDatas);
				arrays.push(datas.PhaseDatas);
			}
			if (arrays.length > 0) {
				handler(arrays);
			}
			arrays = null;
		}
	}
}

class WholePeriodGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "wp";
		this.chartRealType = ChartRealType.WholePeriod;
	}
}

class PolarGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "polar";
		this.chartRealType = ChartRealType.Polar;
	}
}

class CepstrumAnalysisGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "cepstrum";
		this.chartRealType = ChartRealType.CepstrumAnalysis;
	}
}

class CorrelationAnalysisGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "correlation";
		this.chartRealType = ChartRealType.CorrelationAnalysis;
	}
}

class AmplitudeAnalysisGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "ampanalysis";
		this.chartRealType = ChartRealType.AmplitudeAnalysis;
	}
}

class TorvibMonitoringGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.actionName = "torvib";
		this.chartRealType = ChartRealType.TorvibMonitoring;
	}
	onDataReceived(datas) {
		for (const key of this.dataReceiveds.keys()) {
			const handler = this.dataReceiveds.get(key);
			let arrays = datas.filter((r) => r.ComponentId == key);
			if (arrays.length > 0) {
				for (const data of arrays) {
					if (data.Datas?.length == 2) {
						data.Datas = data.Datas[1];
						data.Datas.Datas1 = getFloatPointList(data.Datas.Datas1);
						data.Datas.Datas2 = getFloatPointList(data.Datas.Datas2);
						data.Datas.Datas3 = getFloatPointList(data.Datas.Datas3);
					}
				}
				handler(arrays);
			}
			arrays = null;
		}
	}
}

class RecorderRealGraphHandler extends TimeDomainGraphHandler {
	constructor() {
		super();
	}
	createRegisterRequest(data) {
		data.Source = ChartRealType.RecorderReal;
		data.IsRecorder = store.state.graphSetting.UseRecorder == true;
		return {
			method: "post",
			url: "DataManage/reocrder",
			data: data,
		}
	}
}

class RecorderGraphHandler extends SingleGraphHandlerBase {
	constructor() {
		super();
		this.chartRealType = ChartRealType.Recorder;
	}
	registerPoints(requestId, registerPoints) {
		this.graphRegisterPoints.set(requestId, registerPoints);
		this.sendComponentRegiterPointsToClient(requestId);
	}
	unRegisterPoints(requestId) {
		this.sendComponentUnRegisterPointsToClient(requestId);
		this.graphRegisterPoints.delete(requestId);
	}
	onDataReceived(datas) {
		for (const key of this.dataReceiveds.keys()) {
			const handler = this.dataReceiveds.get(key);
			let arrays = datas.filter((r) => r.ComponentId == key);
			if (arrays.length > 0) {
				console.log(datas);
				let temp = [];
				for (const data of arrays) {
					if (data.Datas?.length == 2 && data.Datas[0] == 11 && data.Datas[1].DataType == 9) {
						if (data.AlgorithmType === 14 || data.AlgorithmType === 106) {
							temp.push(...this.getEnvelopDatas(data));
						} else {
							data.Datas = getDilutePointList(data.Datas[1]);
						}
						temp.push(data);
					}
				}
				handler(temp);
				temp = null;
			}
			arrays = null;
		}
	}
	getEnvelopDatas(data) {
		const result = [];
		const datasTemp = data.Datas[1];
		const dataSegments = datasTemp.DataSegments;
		datasTemp.DataSegments = null;
		data.Datas = null;
		const len = dataSegments.length;
		const up = _.cloneDeep(datasTemp);
		up.DataSegments = dataSegments.slice(0, len / 2);
		result.push(this.createEnvelopData(data, up, 1, i18nGlobal('envelopeJS.upperEnvelope')));
		const low = _.cloneDeep(datasTemp);
		low.DataSegments = dataSegments.slice(len / 2, len);
		result.push(this.createEnvelopData(data, low, 2, i18nGlobal('envelopeJS.lowerEnvelope')));
		return result;
	}
	createEnvelopData(data, datas, flex, name) {
		const tempData = _.cloneDeep(data);
		tempData.PointId += "_" + flex;
		tempData.PointName += "_" + name;
		tempData.Datas = getDilutePointList(datas);
		return tempData;
	}
	sendComponentRegiterPointsToClient(requestId) {
		if ((SignalRInstance.GetInstance()?.Hub?.connection?.connectionId != null) == false) return;
		const registerPoints = this.graphRegisterPoints.get(requestId);
		const postData = {
			ComponentId: requestId,
			ConnectionId: SignalRInstance.GetInstance().Hub.connection.connectionId,
			Source: [this.chartRealType],
			BeginTime: registerPoints.StartTime,
			EndTime: registerPoints.EndTime,
			QueryTime: registerPoints.CurrentTime,
			PointIds: registerPoints.Points.map(p => p.PointId),
		};
		axios({
			method: "post",
			url: `DataManage/query-dilute-datas`,
			data: postData,
		})
	}
	sendComponentUnRegisterPointsToClient() { }
	sendRegisterPointsToClinet() { }
}


class GraphTimerHandler {
	constructor() {
		this.handlers = new Map();
		this.timer = setInterval(() => {
			for (const handler of this.handlers.values()) {
				handler();
			}
		}, 60 * 1000);
	}
	addHandler(key, handler) {
		this.handlers.set(key, handler);
	}
	removeHandler(key) {
		if (this.handlers.has(key)) {
			this.handlers.delete(key);
		}
	}
}

export class GraphTimerContext {
	static _instance = null;
	static GetInstance() {
		if (GraphTimerContext._instance == null) {
			GraphTimerContext._instance = new GraphTimerHandler();
		}
		return ClientRealContext._instance;
	}
}

export class ClientRealContext {
	static _instance = null;
	static GetInstance(chartRealType) {
		if (ClientRealContext._instance == null) {
			ClientRealContext.initial();
		}
		return ClientRealContext._instance.getHandler(chartRealType);
	}
	static initial() {
		let signalRinstance = SignalRInstance.GetInstance();
		ClientRealContext._instance = new GraphSignalrHandler();
		ClientRealContext._instance.registerHandler(ChartRealType.TimeDomain, new TimeDomainGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.EigenBrowse, new BrowEigenHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.Spectral, new SpectrumDomainGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.FreqIntegral, new FreqIntegralGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.CrossPowerSpecDens, new CrossPowerSpectrumDensGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.AxisPosition, new AxisPositionGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.AxisTrail, new AxisTrailGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.BodePlots, new BodePlotsGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.WholePeriod, new WholePeriodGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.Polar, new PolarGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.CablePre, new CablePreGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.AmplitudeAnalysis, new AmplitudeAnalysisGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.CepstrumAnalysis, new CepstrumAnalysisGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.CorrelationAnalysis, new CorrelationAnalysisGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.TorvibMonitoring, new TorvibMonitoringGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.ShaftPower, new ShaftPowerGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.Torque, new TorqueGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.Recorder, new RecorderGraphHandler());
		ClientRealContext._instance.registerHandler(ChartRealType.RecorderReal, new RecorderRealGraphHandler());
		signalRinstance.Add(ClientRealContext._instance);
	}
	static Clear() {
		ClientRealContext._instance.clear();
		ClientRealContext._instance = null;
	}
}

export const getDataPointList = (data) => {
	const xcofe = data.DataType == 0 ? 10000 : 1;
	const point = {
		DataType: data.DataType,
		DataCount: data.DataCount,
		Points: [],
	};
	point.Points = new Array(data.DataCount);
	for (let y = 0; y < data.DataCount; y++) {
		point.Points[y] = {
			X: data.Datas[y].X / xcofe,
			Y: data.Datas[y].Y
		}
	}
	return point;
}

export const getFloatPointList = (data) => {
	const xcofe = data.DataType == 6 ? 10000 : 1;
	const point = {
		DataType: data.DataType,
		DataCount: data.DataCount,
		Points: [],
	};
	point.Points = new Array(data.DataCount);
	for (let y = 0; y < data.DataCount; y++) {
		const x = data.StartX + data.Diff * y;
		point.Points[y] = {
			X: x / xcofe,
			Y: data.Datas[y]
		}
	}
	return point;
}

export const getEnvelopFloatPointList = (data) => {
	const xcofe = data.DataType == 6 ? 10000 : 1;
	const point = {
		DataType: data.DataType,
		DataCount: data.DataCount,
		Points: [],
	};
	point.Points = new Array(data.DataCount);
	const count = data.DataCount / 2;
	for (let y = 0; y < count; y++) {
		const x = (data.StartX + data.Diff * y);
		point.Points[y] = {
			X: x / xcofe,
			Y: data.Datas[y]
		}
		point.Points[y + count] = {
			X: x / xcofe,
			Y: data.Datas[y + count]
		}
	}
	return point;
}

export const getPolarPointList = (data) => {
	const point = {
		DataType: data.DataType,
		DataCount: data.DataCount,
		Points: [],
	};
	point.Points = new Array(data.DataCount);
	for (let y = 0; y < data.DataCount; y++) {
		point.Points[y] = new PolarDataPoint(data.Datas[y].Real, data.Datas[y].Imaginary, data.Datas[y]);
	}
	return point;
}

export const getTrendPointList = (data) => {
	const xcofe = 10000;
	const point = {
		DataType: data.DataType,
		Index: data.Index,
		DataCount: data.DataCount,
		Points: [],
	};
	point.Points = new Array(data.DataCount);
	for (let y = 0; y < data.DataCount; y++) {
		point.Points[y] = new DataPoint(data.Datas[y].X / xcofe, data.Datas[y].Y);
	}
	return point;
}

export const getEnvelopData = (datas) => {
	const result = [];
	for (const data of datas) {
		if (data.AlgorithmType === 14 || data.AlgorithmType === 106) {
			const waveData = data.Datas;
			const lowerData = createEnvelopData(data, waveData, 1, i18nGlobal('envelopeJS.upperEnvelope'));
			const upperData = createEnvelopData(data, waveData, 2, i18nGlobal('envelopeJS.lowerEnvelope'));
			result.push(lowerData);
			result.push(upperData);
			if (waveData?.DataCount > 0) {
				for (let m = 0; m < waveData.DataCount; m++) {
					const n = waveData.Points[m];
					if (m >= waveData.DataCount / 2) {
						upperData.Datas.Points.push(n);
					} else {
						lowerData.Datas.Points.push(n);
					}
				}
			}
		} else {
			result.push(data)
		}
	}
	return result;
}

const createEnvelopData = (data, waveData, flex, name) => {
	data.Datas = null;
	const tempData = _.cloneDeep(data);
	tempData.PointId += "_" + flex;
	if (tempData.Param) {
		tempData.Param.Name += "_" + name;
		tempData.Param.PointId += "_" + flex;
	}
	if (waveData?.DataCount > 0) {
		tempData.Datas = {
			DataCount: waveData.DataCount / 2,
			DataType: waveData.DataType,
			Points: [],
		};
	}
	return tempData;
}

export const getDilutePointList = (data) => {
	const point = {
		DataType: 0,
		DataCount: 0,
		Points: [],
		IsDilute: data.IsDilute,
		DiluteRatio: data.DiluteRatio,
		BeginTime: data.BeginTime / 10000,
		Breaks: []
	};
	data.DataSegments.forEach(item => point.DataCount += item.Datas.length);
	point.Points = new Array(data.DataCount);
	let prex = 0;
	for (const segment of data.DataSegments) {
		if (segment.IsContinous == false && prex != 0) {
			point.Breaks.push(prex);
		}
		if (data.IsDilute) {
			for (let y = 0; y < segment.Datas.length; y++) {
				const x = segment.StartX + segment.Diff * parseInt(y / 2) * data.DiluteRatio;
				point.Points[y + prex] = {
					X: x / 10000,
					Y: segment.Datas[y]
				}
			}
			prex += segment.Datas.length;
		} else {
			for (let y = 0; y < segment.Datas.length; y++) {
				const x = segment.StartX + segment.Diff * y;
				point.Points[y + prex] = {
					X: x / 10000,
					Y: segment.Datas[y]
				}
			}
			prex += segment.Datas.length;
		}

	}
	return point;
}